/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import Dropzone from 'react-dropzone';
import DropzoneUploadLabel from 'components/consumer/Dropzone/DropzoneUploadLabel';
import DropzoneUploadWrapper from 'components/consumer/Dropzone/DropzoneUploadWrapper';
import MediaPreview from './MediaPreview';
import { ACCEPTED_IMAGE_TYPES, ACCEPTED_VIDEO_TYPES, MAX_SIZE_BYTES, MAX_SIZE_MB, MAX_IMAGES, MAX_VIDEOS } from '../../constants';
const IssueMediaUpload = ({
  actions: {
    updateIssueAttachments,
    updateRemovedMediaList
  },
  state: {
    orderCartId,
    removedMediaList
  }
}) => {
  const [mediaList, updateMediaList] = useState([]);
  const [errorMessage, updateErrorMessage] = useState(null);
  const [hideDropBox, updateHideDropBox] = useState(false);
  const getDropRejectionError = rejected => {
    const allAcceptedTypes = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];
    const plural = rejected.length > 1 ? 's' : '';
    const hasWrongType = rejected.some(item => item.errors ? item.errors.some(e => e.code === 'file-invalid-type') : !allAcceptedTypes.includes(item.type));
    const hasTooLarge = rejected.some(item_0 => item_0.errors ? item_0.errors.some(e_0 => e_0.code === 'file-too-large') : item_0.size > MAX_SIZE_BYTES);
    if (hasWrongType && hasTooLarge) {
      return `Some files were rejected. Accepted formats: JPG, PNG, GIF, HEIC images and MOV, MP4, M4V, MPEG videos. Max ${MAX_SIZE_MB} MB per file.`;
    }
    if (hasWrongType) {
      return `Invalid file type${plural}. We accept JPG, PNG, GIF, HEIC images and MOV, MP4, M4V, MPEG videos.`;
    }
    if (hasTooLarge) {
      return `File${plural} too large. Maximum size is ${MAX_SIZE_MB} MB per file.`;
    }
    return `Sorry, there was a problem with your file${plural}. Accepted formats: JPG, PNG, GIF, HEIC images and MOV, MP4, M4V, MPEG videos up to ${MAX_SIZE_MB} MB.`;
  };
  const onFileDrop = (accepted, rejected_0) => {
    updateErrorMessage(null);

    // react-dropzone v8 may pass files with empty MIME type as accepted —
    // enforce the whitelist ourselves.
    const allAcceptedTypes_0 = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];
    const acceptedWithValidMime = accepted.filter(file => allAcceptedTypes_0.includes(file.type));
    const acceptedWithInvalidMime = accepted.filter(file_0 => !allAcceptedTypes_0.includes(file_0.type));
    const allRejected = [...(rejected_0 || []), ...acceptedWithInvalidMime];
    if (allRejected.length > 0) {
      updateErrorMessage(getDropRejectionError(allRejected));
      if (acceptedWithValidMime.length === 0) return null;
    }
    if (acceptedWithValidMime && acceptedWithValidMime.length > 0) {
      let imageFiles = [];
      let videoFiles = [];
      let fileList = mediaList;
      fileList = fileList?.length > 0 ? fileList.concat(acceptedWithValidMime) : acceptedWithValidMime;
      fileList.forEach(file_1 => {
        if (file_1.type.match(/^image\/*/)) {
          imageFiles.push(Object.assign(file_1, {
            preview: URL.createObjectURL(file_1)
          }));
        } else if (file_1.type.match(/^video\/*/)) {
          videoFiles.push(file_1);
        }
      });
      fileList = [];
      if (imageFiles.length >= MAX_IMAGES && videoFiles.length >= MAX_VIDEOS) {
        updateHideDropBox(true);
      }
      if (imageFiles.length > MAX_IMAGES) {
        imageFiles = imageFiles.slice(0, MAX_IMAGES);
        updateErrorMessage(`You've reached the maximum amount of ${MAX_IMAGES} images for submission.`);
        return null;
      }
      if (videoFiles.length > MAX_VIDEOS) {
        videoFiles = videoFiles.slice(0, MAX_VIDEOS);
        updateErrorMessage(`Only one video is allowed for submission.`);
        return null;
      }
      if (videoFiles.length + imageFiles.length === 0) {
        updateErrorMessage(`You must upload atleast one image or video..`);
        return null;
      }
      fileList = imageFiles.length > 0 ? fileList.concat(fileList, imageFiles) : fileList;
      fileList = videoFiles.length > 0 ? fileList.concat(fileList, videoFiles) : fileList;
      fileList = uniqBy(fileList, 'name');
      updateMediaList([...fileList]);
    }
    return null;
  };
  const manageMediaList = () => {
    const attachments = {
      photos: [],
      videos: []
    };
    if (!Array.isArray(mediaList) || mediaList.length === 0) {
      updateIssueAttachments(orderCartId, attachments);
    }
    mediaList.forEach(file_2 => {
      if (file_2.type.match(/^image\/*/)) {
        attachments.photos.push(file_2);
      } else if (file_2.type.match(/^video\/*/)) {
        attachments.videos.push(file_2);
      }
    });
    updateIssueAttachments(orderCartId, attachments);
  };
  const addAttachments = (id, s3Path) => {
    if (mediaList[id] && s3Path) {
      mediaList[id] = Object.assign(mediaList[id], {
        s3Path
      });
    }
    manageMediaList();
  };
  const removeAttachment = id_0 => {
    updateErrorMessage(null);
    removedMediaList.push(mediaList[id_0]);
    mediaList.splice(id_0, 1);
    manageMediaList();
    updateMediaList([...mediaList]);
    updateRemovedMediaList([...removedMediaList]);
    updateHideDropBox(false);
  };
  return <div className="mt-6">
      {mediaList && mediaList.length > 0 && <div className="m-0 grow max-w-full basis-0">
          <ul className="m-0 py-2 relative list-none">
            {mediaList.map((file_3, id_1) => <MediaPreview key={file_3.path} actions={{
          removeAttachment,
          addAttachments
        }} state={{
          file: file_3,
          id: id_1
        }} />)}
          </ul>
        </div>}

      {!!errorMessage && <div data-xc="warranty-form-validation" className="py-2 px-3.5 my-4 border-[1px] border-[#d00] bg-[#FFEBE8] text-sm">
          {errorMessage}
        </div>}

      {!hideDropBox && <DropzoneUploadWrapper>
          <Dropzone accept=".jpg, .jpeg, .png, .gif, .mov, .mp4, .mv4, .m4v, .mpeg4, .heic" multiple maxSize={MAX_SIZE_BYTES} onDrop={onFileDrop}>
            {({
          getRootProps,
          getInputProps,
          isDragActive
        }) => <div {...getRootProps()} className={isDragActive ? 'border-[1px] border-brand-darker border-dotted' : ''}>
                <input autoCorrect="off" {...getInputProps()} />
                <DropzoneUploadLabel subLabel=", 5 photos & 1 video maximum." />
              </div>}
          </Dropzone>
        </DropzoneUploadWrapper>}
    </div>;
};
IssueMediaUpload.propTypes = {
  actions: PropTypes.shape({
    updateIssueAttachments: PropTypes.func,
    updateRemovedMediaList: PropTypes.func
  }),
  state: PropTypes.shape({
    orderCartId: PropTypes.string,
    removedMediaList: PropTypes.array
  })
};
export default React.memo(IssueMediaUpload);